'use client';

import { useEffect, useMemo, useState } from 'react';

import { publicLegalRoutes } from '@/app/(public)/legal/routes';
import {
  useAcceptCurrentTermsMutation,
  useGetCurrentTermsQuery,
} from '@/features/legal/api/legalApi';
import { clearTermsAcceptanceRequired } from '@/features/legal/slices/legalSlice';
import { useAppDispatch } from '@/store/hooks';
import { uiConfig } from '@web/constants';
import { Link, SmartInput } from '@web/ui';
import { formatDate, getErrorMessage, notify } from '@web/utils';

import { LegalTermsPanel } from './LegalTermsPanel';
import styles from './LegalTermsSettingsPanel.module.scss';

type LegalTermsSettingsPanelProps = {
  markdownContent: string;
};

export function LegalTermsSettingsPanel({
  markdownContent,
}: LegalTermsSettingsPanelProps) {
  const dispatch = useAppDispatch();
  const { data: currentTermsResponse } = useGetCurrentTermsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [acceptCurrentTerms, { isLoading: isAccepting }] =
    useAcceptCurrentTermsMutation();

  const [acceptedDraft, setAcceptedDraft] = useState(false);

  const currentTerms =
    currentTermsResponse?.success === true ? currentTermsResponse.data : null;
  const activeVersion =
    currentTerms && 'version_slug' in currentTerms ? currentTerms : null;
  const versionSlug = activeVersion?.version_slug ?? null;
  const effectiveDate = formatDate(
    activeVersion?.effective_at,
    uiConfig.dateFormats.date,
    '',
  );

  const isAccepted = currentTerms?.is_accepted === true;

  useEffect(() => {
    setAcceptedDraft(isAccepted);
  }, [isAccepted]);

  useEffect(() => {
    if (isAccepted) {
      dispatch(clearTermsAcceptanceRequired());
    }
  }, [dispatch, isAccepted]);

  const acceptanceDescription = useMemo(() => {
    if (!activeVersion) {
      return 'No active terms version is available right now.';
    }

    if (isAccepted) {
      return effectiveDate
        ? `Accepted latest terms effective ${effectiveDate}.`
        : 'Accepted latest terms.';
    }

    return effectiveDate
      ? `Latest terms effective ${effectiveDate}. Accept to continue using Net Net.`
      : 'Accept the latest terms to continue using Net Net.';
  }, [activeVersion, effectiveDate, isAccepted]);

  async function handleAcceptedChange(checked: boolean) {
    if (!checked) {
      if (!isAccepted) {
        setAcceptedDraft(false);
      }
      return;
    }

    if (isAccepted || !versionSlug) {
      return;
    }

    setAcceptedDraft(true);
    try {
      const response = await acceptCurrentTerms({
        version_slug: versionSlug,
      }).unwrap();

      if (!response.success) {
        throw new Error(response.message || 'Unable to accept terms.');
      }

      dispatch(clearTermsAcceptanceRequired());
      notify({
        type: 'success',
        message: response.message || 'Terms accepted successfully.',
      });
    } catch (error) {
      setAcceptedDraft(false);
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  }

  return (
    <section className={styles.termsSettingsPanel}>
      <div className={`card-container-glass ${styles.acceptanceCard}`}>
        <SmartInput
          type="checkbox"
          name="terms_acceptance"
          checked={isAccepted || acceptedDraft}
          readOnly={!versionSlug || isAccepted || isAccepting}
          label={`I agree to the latest Terms & Conditions${versionSlug ? ` (${versionSlug})` : ''}`}
          onChange={(nextChecked) => {
            void handleAcceptedChange(Boolean(nextChecked));
          }}
        />
        <p className={styles.acceptanceDescription}>{acceptanceDescription}</p>
        <nav
          className={styles.legalLinks}
          aria-label="Additional legal documents"
        >
          <Link href={publicLegalRoutes.privacy} ariaLabel="Privacy Policy">
            Privacy Policy
          </Link>
          <span
            style={{
              color: 'var(--nn-color-interactive)',
            }}
          >
            •
          </span>
          <Link
            href={publicLegalRoutes.dpa}
            ariaLabel="Data Processing Addendum"
          >
            DPA
          </Link>
        </nav>
      </div>

      <LegalTermsPanel markdownContent={markdownContent} />
    </section>
  );
}
